<template>
  <div class="flex flex-col">
    <t-rich-select :options="answerOptions"
                   :value="intentPlatformId"
                   :tabindex="tabindex"
                   :open-on-focus="true"
                   :placeholder="placeholder"
                   :variant="variant"
                   data-cy="answer-select"
                   value-attribute="intent.platformId"
                   text-attribute="name"
                   no-results-text=""
                   max-height="400"
                   ref="richSelect"
                   @keydown.down.native="handleKeyDown"
                   @focus="handleSelectFocus"
                   @blur="handleSelectBlur"
                   @change="changeAnswer"
    >

      <template slot="option" slot-scope="{ option, isHighlighted, query, index }">
        <div>
          <answer-select-option :answer="option.raw"
                                :active="isOptionActive(option.raw)"
                                :display-example="!isAddNewAnswerShown || index !== 0"
                                :is-highlighted="isHighlighted"
                                class="p-3"
                                :is-add-new-answer-shown="isAddNewAnswerShown && index === 0"
                                :query="processQuery(query)"
          />
        </div>
      </template>

      <template slot="label" slot-scope="{ option }">
        <span class="text-black">{{ option.text || inputText }}</span>
      </template>

      <template slot="noResults">
        <div v-if="!showAddNewAnswer" class="p-3">
          The answer you are searching for does not exist!
        </div>
      </template>

      <template slot="arrow">
        <img v-if="displayClearButton && intentPlatformId"
             :src="redCrossIcon"
             alt="remove selected option"
             @click="$emit('remove-answer')"
        >
        <!--      no props for hiding of search icon-->
        <span v-else class="hidden"/>
      </template>
    </t-rich-select>
    <input-error-message :show-error-message="!!errorMessage" :error-message="errorMessage"/>
  </div>
</template>

<script>
import AnswerSelectOption from './AnswerSelectOption.vue'
import clone from 'ramda/src/clone'
import prepend from 'ramda/src/prepend'
import isEmpty from 'ramda/src/isEmpty'
import redCrossIcon from '@/assets/images/icons/red_cross_icon.svg'
import InputErrorMessage from '@/components/base-components/InputErrorMessage.vue'
import sanitizeString from 'replace-special-characters'
import filter from 'ramda/src/filter'

export default {
  name: 'AnswerSelect',
  components: { InputErrorMessage, AnswerSelectOption },
  props: {
    answerOptions: {
      type: Array,
      required: true
    },
    intentPlatformId: {
      type: String
    },
    answerName: {
      type: String
    },
    firstRequest: {
      type: String
    },
    tabindex: {
      type: Number
    },
    showAddNewAnswer: {
      type: Boolean,
      default: false
    },
    selectVariant: {
      type: String,
      default: 'answerSuggestion'
    },
    selectVariantHiddenDropdown: {
      type: String,
      default: 'answerSuggestionHiddenDropdown'
    },
    displayClearButton: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Search for an Answer...'
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },

  data() {
    return {
      inputText: '',
      variant: this.selectVariant,
      redCrossIcon
    }
  },

  computed: {
    buttonWrapper() {
      return this.$refs.richSelect.$refs.buttonWrapper
    },

    isAddNewAnswerShown() {
      if (!this.showAddNewAnswer || !this.inputText) return false

      return !this.answerOptions.some(option => option.name === this.inputText)
    }
  },

  watch: {
    inputText(value) {
      value ? this.showDropdown() : this.hideDropDown()
    },

    errorMessage: {
      immediate: false,
      handler(errorMessage) {
        this.variant = errorMessage ? `${this.selectVariant}Error` : this.selectVariant
      }
    }
  },

  methods: {
    hideDropDown() {
      this.variant = this.selectVariantHiddenDropdown
    },

    showDropdown() {
      this.variant = this.selectVariant
    },

    async handleSelectFocus() {
      this.buttonWrapper.classList.add('h-0')
      this.$refs.richSelect.query = this.answerName
      // a time for dropdown to render
      await this.$nextTick()
      this.showDropdown()
    },

    handleSelectBlur() {
      this.$emit('blur')
      this.buttonWrapper.classList.remove('h-0')
    },

    isOptionActive(answer) {
      return answer?.intent?.platformId === this.intentPlatformId
    },

    handleKeyDown() {
      if (!this.$refs.richSelect.$refs.optionsList?.classList.contains('hidden')) return

      this.showDropdown()
      this.$refs.richSelect.highlighted = 0
    },

    processQuery(query) {
      this.inputText = query
      return query
    },

    changeAnswer(selectedPlatformId) {
      // prevents to fire change event if cross is clicked
      if (this.intentPlatformId === selectedPlatformId) return

      const { text, value: platformId } = this.$refs.richSelect.highlightedOption
      this.emitAnswer(platformId, text)
    },

    async focusOnAnswerSelect() {
      if (this.intentPlatformId) return

      this.$refs.richSelect.focus()
      await this.$nextTick()
      this.hideDropDown()
    },

    emitAnswer(intentPlatformId, answerName) {
      this.$emit('change-answer', { intentPlatformId, answerName })
    },

    async filterOptions(query) {
      const optionsCopy = clone(this.$refs.richSelect.normalizedOptions)
      if (!query) return optionsCopy

      const term = sanitizeString(query.toLowerCase())
      const filterByText = ({ text }) => sanitizeString(text.toLowerCase()).includes(term)
      const filteredOptions = filter(filterByText, optionsCopy)

      const newAnswerOption = {
        text: query,
        value: query,
        raw: { intent: { platformId: null } }
      }

      // inputText isn't set in time, so isAddNewAnswerShown isn't updated
      await this.$nextTick()
      const filteredOptionsCopy = this.isAddNewAnswerShown ?
        prepend(newAnswerOption, filteredOptions) :
        filteredOptions.filter(({ text }) => text !== 'addNewAnswer')

      this.$refs.richSelect.highlighted = isEmpty(filteredOptionsCopy) ? null : 0
      this.$refs.richSelect.filteredOptions = filteredOptionsCopy
    }
  },

  mounted() {
    this.focusOnAnswerSelect()
    // to exclude add new answer option from search
    this.$refs.richSelect.filterOptions = this.filterOptions
  }
}
</script>
